import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Download, Plus, Check, X, AlertTriangle, Clock, Trash2, FileText, Settings, CalendarDays, ChevronLeft, ChevronRight, Receipt, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import api from "@/lib/api";
import { listQuotes, exportUrl } from "@/lib/api";
import { formatCurrency } from "@/lib/calculations";
import { AiReceiptCategoriser } from "@/components/custom/AiFeatures";
import IntegrationsPanel from "@/components/custom/IntegrationsPanel";
import QbSyncChip from "@/components/custom/QbSyncChip";
import { format, parse, isValid } from "date-fns";

function daysSince(d) { if (!d) return 0; try { return Math.floor((Date.now() - new Date(d).getTime()) / 86400000); } catch { return 0; } }

function fmtDate(dateStr) {
  if (!dateStr) return "—";
  try {
    let d;
    if (dateStr.includes("T")) d = new Date(dateStr);
    else if (dateStr.includes("/")) { const p = dateStr.split("/"); d = p[2]?.length === 4 ? new Date(p[2], p[1]-1, p[0]) : new Date(2000+parseInt(p[2]||"0"), p[1]-1, p[0]); }
    else d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${String(d.getFullYear()).slice(-2)}`;
  } catch { return dateStr; }
}

export default function Financials() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [quotes, setQuotes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [vatQuarters, setVatQuarters] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(searchParams.get("tab") || "vat");
  // Keep URL ?tab=... in sync so deep links + the OAuth callback land on the right tab
  useEffect(() => {
    const cur = searchParams.get("tab");
    if (tab && tab !== cur) {
      const next = new URLSearchParams(searchParams);
      next.set("tab", tab);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
  const [selectedQuarter, setSelectedQuarter] = useState(null); // null = current quarter
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showVatSettings, setShowVatSettings] = useState(false);
  const [newExpense, setNewExpense] = useState({ merchant: "", description: "", amount: "", vat_amount: "", date: "", category: "materials" });
  const [expDateOpen, setExpDateOpen] = useState(false);
  const [editQuarters, setEditQuarters] = useState({});
  // Date range filter (for Expenses, Outstanding, Contractor Pay — NOT VAT)
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  // Contractor Pay
  const [teamMembers, setTeamMembers] = useState([]);
  const [contractorRates, setContractorRates] = useState({});
  const [payRecords, setPayRecords] = useState([]);
  const [selectedMember, setSelectedMember] = useState("");
  const [payMonth, setPayMonth] = useState(() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,"0")}`; });
  const [daysData, setDaysData] = useState(null);
  const [showAddPay, setShowAddPay] = useState(false);
  const [showRates, setShowRates] = useState(false);
  const [showDaysDetail, setShowDaysDetail] = useState(false);
  const [newPay, setNewPay] = useState({ pay_date: "", days_worked: "", hours_worked: "", gross_pay: "" });

  const fetchData = useCallback(async () => {
    try {
      const [q, e, vq, tm, cr, pr] = await Promise.all([
        listQuotes(),
        api.get("/expenses").then(r => r.data),
        api.get("/settings/vat-quarters").then(r => r.data),
        api.get("/team-members").then(r => r.data.filter(m => m.member_type !== "sub_contractor")),
        api.get("/settings/contractor-rates").then(r => r.data),
        api.get("/pay-records").then(r => r.data),
      ]);
      setQuotes(q); setExpenses(e); setVatQuarters(vq);
      setTeamMembers(tm); setContractorRates(cr); setPayRecords(pr);
      if (!selectedMember && tm.length > 0) setSelectedMember(tm[0].id);
    } catch (err) { console.error("Failed to load financials data:", err); } finally { setLoading(false); }
  }, [selectedMember]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch days worked when member or month changes
  useEffect(() => {
    if (!selectedMember || !payMonth) return;
    api.get(`/contractor-days/${selectedMember}?month=${payMonth}`).then(r => setDaysData(r.data)).catch(() => {});
  }, [selectedMember, payMonth]);

  // Current VAT quarter
  const currentQuarter = useMemo(() => {
    const now = new Date();
    const m = now.getMonth() + 1;
    const d = now.getDate();
    for (const [qn, qv] of Object.entries(vatQuarters)) {
      // Format is DD/MM
      const startParts = (qv.start || "").split("/").map(Number);
      const endParts = (qv.end || "").split("/").map(Number);
      const startDay = startParts[0], startMonth = startParts[1];
      const endDay = endParts[0], endMonth = endParts[1];
      if (!startMonth || !endMonth) continue;
      const afterStart = m > startMonth || (m === startMonth && d >= startDay);
      const beforeEnd = m < endMonth || (m === endMonth && d <= endDay);
      if (startMonth <= endMonth) {
        // Normal range (e.g., Mar-May)
        if (afterStart && beforeEnd) return qn;
      } else {
        // Wraps around year (e.g., Dec-Feb)
        if (afterStart || beforeEnd) return qn;
      }
    }
    return "Q1";
  }, [vatQuarters]);

  // Active quarter for VAT display (selectedQuarter overrides currentQuarter)
  const activeQuarter = selectedQuarter || currentQuarter;

  // VAT calculations for active quarter
  const vatData = useMemo(() => {
    const qDates = vatQuarters[activeQuarter];
    if (!qDates) return { output: 0, input: 0, net: 0, invoiced: [], expensesList: [] };
    const startParts = (qDates.start || "").split("/").map(Number);
    const endParts = (qDates.end || "").split("/").map(Number);
    const startDay = startParts[0], startMonth = startParts[1];
    const endDay = endParts[0], endMonth = endParts[1];
    const now = new Date();
    const year = now.getFullYear();

    const inQuarter = (dateStr) => {
      if (!dateStr) return false;
      try {
        const d = new Date(dateStr);
        const m = d.getMonth() + 1, day = d.getDate();
        const afterStart = m > startMonth || (m === startMonth && day >= startDay);
        const beforeEnd = m < endMonth || (m === endMonth && day <= endDay);
        if (startMonth <= endMonth) return afterStart && beforeEnd;
        return afterStart || beforeEnd;
      } catch { return false; }
    };

    const invoiced = quotes.filter(q => q.invoice_paid && inQuarter(q.invoice_paid_date || q.updated_at));
    const output = invoiced.reduce((s, q) => s + (q.vat_amount || 0), 0);
    const qExpenses = expenses.filter(e => e.confirmed && inQuarter(e.date ? `${e.date.split("/").reverse().join("-")}` : ""));
    const input = qExpenses.reduce((s, e) => s + (e.vat_amount || 0), 0);

    return { output: Math.round(output * 100) / 100, input: Math.round(input * 100) / 100, net: Math.round((output - input) * 100) / 100, invoiced, expensesList: qExpenses };
  }, [quotes, expenses, vatQuarters, activeQuarter]);

  // Outstanding payments — days based on first_sent_date (preserved across resends), falling back to updated_at for legacy records.
  // Now includes "Partially Paid" jobs so the short-paid balance continues to be chased.
  const outstanding = useMemo(() => {
    return quotes
      .filter(q => (q.status === "Invoiced" || q.status === "Partially Paid") && q.payment_status !== "paid" && (!q.invoice_paid || q.payment_status === "partial"))
      .map(q => {
        const paid = +(q.invoice_paid_amount || 0);
        const total = +(q.total_inc_vat || 0);
        const balance = q.payment_status === "partial" ? Math.max(total - paid, 0) : total;
        return { ...q, days: daysSince(q.first_sent_date || q.updated_at), outstanding_balance: balance, paid_so_far: paid };
      })
      .sort((a, b) => b.days - a.days);
  }, [quotes]);

  // Date range filter helper (does NOT affect VAT)
  const inDateRange = (dateStr) => {
    if (!filterFrom && !filterTo) return true;
    if (!dateStr) return false;
    try {
      // Handle multiple date formats
      let d;
      if (dateStr.includes("T")) d = new Date(dateStr); // ISO
      else if (dateStr.includes("/")) { const p = dateStr.split("/"); d = p[2] ? new Date(p[2], p[1]-1, p[0]) : new Date(dateStr); }
      else d = new Date(dateStr);
      if (isNaN(d)) return false;
      const ts = d.getTime();
      if (filterFrom) { const from = new Date(filterFrom); from.setHours(0,0,0,0); if (ts < from.getTime()) return false; }
      if (filterTo) { const to = new Date(filterTo); to.setHours(23,59,59,999); if (ts > to.getTime()) return false; }
      return true;
    } catch { return false; }
  };

  const filteredExpenses = useMemo(() => {
    if (!filterFrom && !filterTo) return expenses;
    return expenses.filter(e => {
      const dateStr = e.date ? (e.date.includes("/") ? e.date.split("/").reverse().join("-") : e.date) : e.created_at;
      return inDateRange(dateStr);
    });
  }, [expenses, filterFrom, filterTo]);

  const filteredOutstanding = useMemo(() => {
    if (!filterFrom && !filterTo) return outstanding;
    return outstanding.filter(q => inDateRange(q.updated_at));
  }, [outstanding, filterFrom, filterTo]);

  const handleAddExpense = async () => {
    if (!newExpense.merchant || !newExpense.amount) { toast.error("Merchant and amount required"); return; }
    try {
      await api.post("/expenses", { ...newExpense, amount: parseFloat(newExpense.amount) || 0, vat_amount: parseFloat(newExpense.vat_amount) || 0 });
      toast.success("Expense added");
      setShowAddExpense(false);
      setNewExpense({ merchant: "", description: "", amount: "", vat_amount: "", date: "", category: "materials" });
      fetchData();
    } catch { toast.error("Failed"); }
  };

  const handleConfirmExpense = async (id) => {
    await api.put(`/expenses/${id}`, { confirmed: true });
    fetchData();
  };

  const handleDeleteExpense = async (id) => {
    await api.delete(`/expenses/${id}`);
    fetchData();
  };

  const handleSaveQuarters = async () => {
    await api.put("/settings/vat-quarters", editQuarters);
    setVatQuarters(editQuarters);
    setShowVatSettings(false);
    toast.success("VAT quarters saved");
  };

  // Auto-import material expenses from ordered items
  const autoImportMaterials = async () => {
    const existingQuoteIds = new Set(expenses.map(e => e.quote_id));
    let imported = 0;
    for (const q of quotes) {
      if (!q.items || q.items.length === 0) continue;
      for (const item of q.items) {
        if (!item.ordered) continue;
        const expKey = `${q.id}_${item.id}`;
        if (existingQuoteIds.has(expKey)) continue;
        const total = (item.unit_price || 0) * (item.qty || 0);
        const vat = Math.round(total * 0.2 * 100) / 100;
        await api.post("/expenses", {
          merchant: item.supplier || "Unknown",
          description: `${item.item_name || "Material"} - ${q.site_address || ""}`,
          amount: total,
          vat_amount: vat,
          date: new Date().toLocaleDateString("en-GB"),
          quote_id: expKey,
          category: "materials",
          confirmed: false,
        });
        imported++;
      }
    }
    if (imported > 0) { toast.success(`${imported} material expenses imported`); fetchData(); }
    else toast.info("No new materials to import");
  };

  const TABS = [
    { id: "vat", label: "VAT Returns" },
    { id: "integrations", label: "Integrations" },
    { id: "expenses", label: "Expenses" },
    { id: "outstanding", label: "Outstanding" },
    { id: "pay", label: "Contractor Pay" },
    { id: "profitability", label: "Job Profitability" },
    { id: "quoter_earnings", label: "Quote Author Earnings" },
  ];

  const memberRates = selectedMember ? (contractorRates[selectedMember] || {}) : {};
  const memberPayRecords = payRecords.filter(r => r.team_member_id === selectedMember);
  const filteredPayRecords = useMemo(() => {
    if (!filterFrom && !filterTo) return memberPayRecords;
    return memberPayRecords.filter(r => inDateRange(r.pay_date));
  }, [memberPayRecords, filterFrom, filterTo]);
  const memberObj = teamMembers.find(m => m.id === selectedMember);
  const monthCIS = memberPayRecords.filter(r => r.pay_date?.startsWith(payMonth)).reduce((s,r) => s + (r.cis_deduction || 0), 0);
  const monthGross = memberPayRecords.filter(r => r.pay_date?.startsWith(payMonth)).reduce((s,r) => s + (r.gross_pay || 0), 0);
  const monthNet = memberPayRecords.filter(r => r.pay_date?.startsWith(payMonth)).reduce((s,r) => s + (r.net_pay || 0), 0);

  const handleSaveRates = async () => {
    await api.put("/settings/contractor-rates", contractorRates);
    toast.success("Rates saved");
    setShowRates(false);
  };

  const handleAddPayRecord = async () => {
    if (!newPay.pay_date || !newPay.gross_pay) { toast.error("Date and gross pay required"); return; }
    const gross = parseFloat(newPay.gross_pay) || 0;
    const cisRate = memberRates.cis_rate || 20;
    const cisDed = Math.round(gross * (cisRate / 100) * 100) / 100;
    const net = Math.round((gross - cisDed) * 100) / 100;
    await api.post("/pay-records", {
      team_member_id: selectedMember,
      team_member_name: memberObj?.name || "",
      pay_date: newPay.pay_date,
      days_worked: parseFloat(newPay.days_worked) || 0,
      hours_worked: parseFloat(newPay.hours_worked) || 0,
      daily_rate: memberRates.daily_rate || 0,
      hourly_rate: memberRates.hourly_rate || 0,
      gross_pay: gross,
      cis_rate: cisRate,
      cis_deduction: cisDed,
      net_pay: net,
    });
    toast.success("Pay record added");
    setShowAddPay(false);
    setNewPay({ pay_date: "", days_worked: "", hours_worked: "", gross_pay: "" });
    fetchData();
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}><p style={{ color: "var(--text-muted)" }}>Loading...</p></div>;

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <div className="px-8 pt-8 pb-4">
        <div className="flex items-center justify-between mb-4">
          <h1 className="ui-page-title">Financials</h1>
          <div className="flex gap-2">
            <a href={exportUrl("expenses")} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium" style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--text-secondary)" }}>
              <Download className="w-3.5 h-3.5" /> Export Expenses
            </a>
            <a href={exportUrl("outstanding")} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium" style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--text-secondary)" }}>
              <Download className="w-3.5 h-3.5" /> Export Outstanding
            </a>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-4">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className="px-4 py-2 rounded-lg text-sm font-medium transition-colors" style={{ background: tab === t.id ? "var(--primary)" : "var(--surface)", color: tab === t.id ? "#fff" : "var(--text-secondary)", border: `1px solid ${tab === t.id ? "var(--primary)" : "var(--border)"}` }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Date range filter (not for VAT or Integrations tabs) */}
        {tab !== "vat" && tab !== "integrations" && (
          <div className="flex items-center gap-3 mb-6">
            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Filter by date:</span>
            <Input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} className="w-40 h-9 rounded-lg text-sm" style={{ border: "1px solid var(--border)" }} data-testid="filter-from" />
            <span className="text-xs" style={{ color: "var(--text-muted)" }}>to</span>
            <Input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)} className="w-40 h-9 rounded-lg text-sm" style={{ border: "1px solid var(--border)" }} data-testid="filter-to" />
            {(filterFrom || filterTo) && (
              <button onClick={() => { setFilterFrom(""); setFilterTo(""); }} className="text-xs font-semibold" style={{ color: "var(--danger)" }}>Clear</button>
            )}
          </div>
        )}
      </div>

      <div className="px-8 pb-8">
        {/* VAT RETURNS TAB */}
        {tab === "vat" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={() => { const keys = Object.keys(vatQuarters); const idx = keys.indexOf(activeQuarter); if (idx > 0) setSelectedQuarter(keys[idx - 1]); else setSelectedQuarter(keys[keys.length - 1]); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors" style={{ border: "1px solid var(--border)" }}>
                  <ChevronLeft className="w-4 h-4" style={{ color: "var(--text-secondary)" }} />
                </button>
                <h2 className="ui-section-title">VAT Period: {activeQuarter} ({vatQuarters[activeQuarter]?.start} – {vatQuarters[activeQuarter]?.end})</h2>
                <button onClick={() => { const keys = Object.keys(vatQuarters); const idx = keys.indexOf(activeQuarter); if (idx < keys.length - 1) setSelectedQuarter(keys[idx + 1]); else setSelectedQuarter(keys[0]); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors" style={{ border: "1px solid var(--border)" }}>
                  <ChevronRight className="w-4 h-4" style={{ color: "var(--text-secondary)" }} />
                </button>
                {selectedQuarter && selectedQuarter !== currentQuarter && (
                  <button onClick={() => setSelectedQuarter(null)} className="text-xs font-semibold px-2 py-1 rounded" style={{ color: "var(--primary)" }}>Current</button>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={() => { setEditQuarters({...vatQuarters}); setShowVatSettings(true); }} className="gap-1 text-xs"><Settings className="w-3.5 h-3.5" /> Edit Quarters</Button>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="ui-surface p-5">
                <p className="ui-label">VAT Collected (Output)</p>
                <p className="text-3xl font-mono font-bold mt-2" style={{ color: "#16a34a" }}>{formatCurrency(vatData.output)}</p>
                <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{vatData.invoiced.length} invoices this period</p>
              </div>
              <div className="ui-surface p-5">
                <p className="ui-label">VAT Reclaimable (Input)</p>
                <p className="text-3xl font-mono font-bold mt-2" style={{ color: "#16a34a" }}>{formatCurrency(vatData.input)}</p>
                <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{vatData.expensesList.length} confirmed expenses</p>
              </div>
              <div className="ui-surface p-5">
                <p className="ui-label">VAT Liability (Owed to HMRC)</p>
                <p className="text-3xl font-mono font-bold mt-2" style={{ color: vatData.net >= 0 ? "var(--danger)" : "#16a34a" }}>{formatCurrency(vatData.net)}</p>
                <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{vatData.net >= 0 ? "You owe HMRC" : "HMRC owes you"}</p>
              </div>
            </div>
            {/* Period breakdown */}
            <div className="ui-surface overflow-hidden">
              <div className="px-5 py-3 border-b" style={{ borderColor: "var(--border)" }}>
                <p className="text-sm font-semibold">Invoices in {activeQuarter}</p>
              </div>
              <table className="ui-table">
                <thead><tr><th>Quote #</th><th>Address</th><th>Client</th><th className="text-right">Ex VAT</th><th className="text-right">VAT</th><th className="text-right">Total</th></tr></thead>
                <tbody>
                  {vatData.invoiced.length === 0 ? <tr><td colSpan={6} className="text-center py-6" style={{ color: "var(--text-muted)" }}>No invoices this period</td></tr> :
                  vatData.invoiced.map(q => (
                    <tr key={q.id}><td className="font-mono">{q.quote_number}</td><td>{q.site_address?.split(",")[0]}</td><td>{q.client_type}</td><td className="text-right font-mono">{formatCurrency(q.total_ex_vat)}</td><td className="text-right font-mono">{formatCurrency(q.vat_amount)}</td><td className="text-right font-mono font-bold">{formatCurrency(q.total_inc_vat)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Refund flag */}
            {(() => {
              const refundedJobs = vatData.invoiced.filter(q => (q.refunds || []).length > 0 || q.total_refunded > 0);
              if (refundedJobs.length === 0) return null;
              const totalRefunds = refundedJobs.reduce((s, q) => s + (q.total_refunded || 0), 0);
              return (
                <div className="mt-3 p-3 rounded-lg text-xs" style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #F59E0B" }}>
                  {refundedJobs.length} refund{refundedJobs.length > 1 ? "s" : ""} issued this period (total: {formatCurrency(totalRefunds)}) — review with your accountant for VAT credit note requirements.
                </div>
              );
            })()}
          </div>
        )}

        {/* INTEGRATIONS TAB (Phase 1: connection lifecycle only) */}
        {tab === "integrations" && (
          <IntegrationsPanel currentUserName={typeof window !== "undefined" ? (localStorage.getItem("desktop_user_name") || "") : ""} />
        )}

        {/* EXPENSES TAB */}
        {tab === "expenses" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="ui-section-title">Expenses</h2>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={autoImportMaterials} className="gap-1 text-xs"><FileText className="w-3.5 h-3.5" /> Import from Materials</Button>
                <AiReceiptCategoriser onApply={async (result) => {
                  try {
                    await api.post("/expenses", { merchant: result.merchant, description: result.description, amount: result.amount || 0, vat_amount: result.vat_amount || 0, date: (() => { const d = new Date(); return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${String(d.getFullYear()).slice(-2)}`; })(), category: result.category || "other", confirmed: false });
                    fetchExpenses();
                  } catch { toast.error("Failed to create expense"); }
                }} />
                <Button size="sm" onClick={() => setShowAddExpense(true)} className="btn-primary gap-1 text-xs"><Plus className="w-3.5 h-3.5" /> Add Expense</Button>
              </div>
            </div>
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4">
              <div className="ui-surface p-4"><p className="ui-label">Total Expenses</p><p className="text-xl font-mono font-bold mt-1">{formatCurrency(filteredExpenses.reduce((s,e) => s + (e.amount||0), 0))}</p></div>
              <div className="ui-surface p-4"><p className="ui-label">Total VAT Reclaimable</p><p className="text-xl font-mono font-bold mt-1" style={{ color: "var(--success)" }}>{formatCurrency(filteredExpenses.filter(e=>e.confirmed).reduce((s,e) => s + (e.vat_amount||0), 0))}</p></div>
              <div className="ui-surface p-4"><p className="ui-label">Unconfirmed</p><p className="text-xl font-mono font-bold mt-1" style={{ color: "var(--warning)" }}>{filteredExpenses.filter(e=>!e.confirmed).length}</p></div>
            </div>
            {/* List */}
            <div className="ui-surface overflow-hidden">
              <table className="ui-table">
                <thead><tr><th>Date</th><th>Merchant</th><th>Description</th><th>Category</th><th className="text-right">Amount</th><th className="text-right">VAT</th><th className="text-center">Status</th><th className="text-center">QB</th><th></th></tr></thead>
                <tbody>
                  {filteredExpenses.length === 0 ? <tr><td colSpan={9}><div className="flex flex-col items-center py-14"><Receipt className="w-10 h-10 mb-4" style={{ color: "#9CA3AF" }} /><p style={{ fontSize: "15px", fontWeight: 600, color: "#374151" }}>No expenses{(filterFrom || filterTo) ? " in this date range" : " recorded"}</p><p style={{ fontSize: "13px", color: "#6B7280", marginTop: "8px" }}>Add expenses manually or import from your bank</p></div></td></tr> :
                  filteredExpenses.map(e => (
                    <tr key={e.id}>
                      <td className="text-xs">{fmtDate(e.date)}</td>
                      <td className="font-medium">{e.merchant}</td>
                      <td className="text-xs max-w-[200px] truncate" style={{ color: "var(--text-secondary)" }}>{e.description}</td>
                      <td><span className="badge-pill badge-gray text-[10px]">{e.category}</span></td>
                      <td className="text-right font-mono">{formatCurrency(e.amount)}</td>
                      <td className="text-right font-mono">
                        <div className="flex items-center justify-end gap-1.5">
                          <span style={{ color: e.vat_amount > 0 ? "var(--success)" : "var(--text-muted)" }}>{formatCurrency(e.vat_amount)}</span>
                          <button
                            onClick={async () => {
                              const hasVat = e.vat_amount > 0;
                              const newVat = hasVat ? 0 : Math.round(((e.amount || 0) / 6) * 100) / 100;
                              try {
                                await api.put(`/expenses/${e.id}`, { vat_amount: newVat });
                                setExpenses(prev => prev.map(ex => ex.id === e.id ? { ...ex, vat_amount: newVat } : ex));
                              } catch (err) { console.error(err); toast.error("Failed to update VAT"); }
                            }}
                            className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-colors"
                            style={{ background: e.vat_amount > 0 ? "var(--success)" : "transparent", border: `2px solid ${e.vat_amount > 0 ? "var(--success)" : "var(--border)"}` }}
                            title={e.vat_amount > 0 ? "VAT active — click to remove" : "Click to add 20% VAT"}
                          >
                            {e.vat_amount > 0 && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                          </button>
                        </div>
                      </td>
                      <td className="text-center">{e.confirmed ? <span className="badge-pill badge-success text-[10px]">Confirmed</span> : <span className="badge-pill badge-warning text-[10px]">Pending</span>}</td>
                      <td className="text-center" onClick={(ev) => ev.stopPropagation()}><QbSyncChip record={e} kind="expense" onSynced={() => loadAll()} /></td>
                      <td>
                        <div className="flex gap-1 justify-end">
                          {!e.confirmed && <button onClick={() => handleConfirmExpense(e.id)} className="p-1 rounded hover:bg-green-50" style={{ color: "var(--success)" }}><Check className="w-3.5 h-3.5" /></button>}
                          <button onClick={() => handleDeleteExpense(e.id)} className="p-1 rounded hover:bg-red-50" style={{ color: "var(--text-muted)" }}><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* OUTSTANDING PAYMENTS TAB */}
        {tab === "outstanding" && (
          <div className="space-y-4">
            <h2 className="ui-section-title">Outstanding Payments</h2>
            <div className="grid grid-cols-3 gap-4">
              <div className="ui-surface p-4"><p className="ui-label">Total Outstanding</p><p className="text-xl font-mono font-bold mt-1">{formatCurrency(filteredOutstanding.reduce((s,q) => s + (q.outstanding_balance ?? q.total_inc_vat ?? 0), 0))}</p></div>
              <div className="ui-surface p-4"><p className="ui-label">Invoices Sent</p><p className="text-xl font-mono font-bold mt-1">{filteredOutstanding.length}</p></div>
              <div className="ui-surface p-4"><p className="ui-label">Avg Days Outstanding</p><p className="text-xl font-mono font-bold mt-1">{filteredOutstanding.length > 0 ? Math.round(filteredOutstanding.reduce((s,q)=>s+q.days,0)/filteredOutstanding.length) : 0}</p></div>
            </div>
            <div className="ui-surface overflow-hidden">
              <table className="ui-table">
                <thead><tr><th>Quote #</th><th>Address</th><th>Client</th><th>Email</th><th className="text-right">Amount</th><th className="text-center">Days</th><th className="text-center">Status</th></tr></thead>
                <tbody>
                  {filteredOutstanding.length === 0 ? <tr><td colSpan={7}><div className="flex flex-col items-center py-14"><CheckCircle2 className="w-10 h-10 mb-4" style={{ color: "#16a34a" }} /><p style={{ fontSize: "15px", fontWeight: 600, color: "#16a34a" }}>All invoices paid — great work!</p><p style={{ fontSize: "13px", color: "#6B7280", marginTop: "8px" }}>{(filterFrom || filterTo) ? "No outstanding invoices in this date range" : "No outstanding invoices to chase"}</p></div></td></tr> :
                  filteredOutstanding.map(q => (
                    <tr key={q.id}>
                      <td className="font-mono">{q.quote_number}</td>
                      <td className="font-medium max-w-[180px] truncate">{q.site_address}</td>
                      <td>{[q.client_first_name, q.client_last_name].filter(Boolean).join(" ")}</td>
                      <td className="text-xs" style={{ color: "var(--text-muted)" }}>{q.correspondence_email}</td>
                      <td className="text-right font-mono font-bold">
                        {q.payment_status === "partial" ? (
                          <>
                            {formatCurrency(q.outstanding_balance)}
                            <span className="block text-[10px] font-normal" style={{ color: "var(--text-muted)" }}>outstanding · {formatCurrency(q.paid_so_far)} part paid</span>
                          </>
                        ) : formatCurrency(q.total_inc_vat)}
                      </td>
                      <td className="text-center" title={`First sent: ${q.first_sent_date ? new Date(q.first_sent_date).toLocaleString() : "—"}\nLast sent: ${q.last_sent_date ? new Date(q.last_sent_date).toLocaleString() : (q.updated_at ? new Date(q.updated_at).toLocaleString() : "—")}`}><span className={`font-mono font-bold ${q.days >= 30 ? "text-red-500" : q.days >= 14 ? "text-amber-500" : ""}`}>{q.days}</span></td>
                      <td className="text-center">
                        {q.days >= 30 ? <span className="badge-pill badge-danger text-[10px]">Overdue</span> :
                         q.days >= 14 ? <span className="badge-pill badge-warning text-[10px]">Follow up</span> :
                         <span className="badge-pill badge-success text-[10px]">Current</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* CONTRACTOR PAY TAB */}
        {tab === "pay" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <Select value={selectedMember} onValueChange={setSelectedMember}>
                  <SelectTrigger className="w-48 rounded-lg"><SelectValue placeholder="Select member" /></SelectTrigger>
                  <SelectContent>{teamMembers.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                </Select>
                <Input type="month" value={payMonth} onChange={e => setPayMonth(e.target.value)} className="w-40 rounded-lg" />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowDaysDetail(true)} className="gap-1 text-xs"><Clock className="w-3.5 h-3.5" /> Days Worked</Button>
                <Button variant="outline" size="sm" onClick={() => setShowRates(true)} className="gap-1 text-xs"><Settings className="w-3.5 h-3.5" /> Rates &amp; CIS</Button>
                <Button size="sm" onClick={() => setShowAddPay(true)} className="btn-primary gap-1 text-xs"><Plus className="w-3.5 h-3.5" /> Add Pay Record</Button>
                <a href={exportUrl("pay-records")} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium" style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--text-secondary)" }}><Download className="w-3.5 h-3.5" /> CSV</a>
              </div>
            </div>

            {/* Month summary cards */}
            <div className="grid grid-cols-4 gap-4">
              <div className="ui-surface p-4">
                <p className="ui-label">Days Worked</p>
                <p className="text-xl font-mono font-bold mt-1">{daysData?.days_worked || 0}</p>
                <p className="text-xs" style={{ color: "var(--text-muted)" }}>{daysData?.absence_days || 0} absence days</p>
              </div>
              <div className="ui-surface p-4">
                <p className="ui-label">Gross Pay (Month)</p>
                <p className="text-xl font-mono font-bold mt-1">{formatCurrency(monthGross)}</p>
              </div>
              <div className="ui-surface p-4">
                <p className="ui-label">CIS Deductions</p>
                <p className="text-xl font-mono font-bold mt-1" style={{ color: "var(--danger)" }}>{formatCurrency(monthCIS)}</p>
                <p className="text-xs" style={{ color: "var(--text-muted)" }}>{memberRates.cis_rate || 20}% rate</p>
              </div>
              <div className="ui-surface p-4">
                <p className="ui-label">Net Pay (Month)</p>
                <p className="text-xl font-mono font-bold mt-1" style={{ color: "var(--success)" }}>{formatCurrency(monthNet)}</p>
              </div>
            </div>

            {/* Pay records table */}
            <div className="ui-surface overflow-hidden">
              <table className="ui-table">
                <thead><tr><th>Pay Date</th><th>Days</th><th>Hours</th><th className="text-right">Gross</th><th className="text-right">CIS ({memberRates.cis_rate || 20}%)</th><th className="text-right">Net</th><th>Invoice</th><th className="text-center">QB</th><th></th></tr></thead>
                <tbody>
                  {filteredPayRecords.length === 0 ? <tr><td colSpan={9}><div className="flex flex-col items-center py-14"><FileText className="w-10 h-10 mb-4" style={{ color: "#9CA3AF" }} /><p style={{ fontSize: "15px", fontWeight: 600, color: "#374151" }}>No pay records for {memberObj?.name}{(filterFrom || filterTo) ? " in this date range" : ""}</p><p style={{ fontSize: "13px", color: "#6B7280", marginTop: "8px", textAlign: "center", maxWidth: "380px" }}>Add a pay record manually or check that the contractor's daily and hourly rates are configured in <button onClick={() => setShowRates(true)} className="underline font-medium" style={{ color: "var(--primary)" }}>Rates &amp; CIS</button></p><button onClick={() => setShowAddPay(true)} className="mt-5 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold" style={{ background: "#E05C2A", color: "#fff" }}><Plus className="w-4 h-4" /> Add Pay Record</button></div></td></tr> :
                  filteredPayRecords.map(r => (
                    <tr key={r.id}>
                      <td className="font-mono text-sm">{r.pay_date}</td>
                      <td className="font-mono">{r.days_worked}</td>
                      <td className="font-mono">{r.hours_worked}</td>
                      <td className="text-right font-mono">{formatCurrency(r.gross_pay)}</td>
                      <td className="text-right font-mono" style={{ color: "var(--danger)" }}>{formatCurrency(r.cis_deduction)}</td>
                      <td className="text-right font-mono font-bold" style={{ color: "var(--success)" }}>{formatCurrency(r.net_pay)}</td>
                      <td>
                        {r.invoice_filename ? (
                          <a href={`${process.env.REACT_APP_BACKEND_URL.replace("/api","")}/api/media/${r.invoice_path}`} target="_blank" rel="noopener noreferrer" className="text-xs" style={{ color: "var(--primary)" }}>{r.invoice_filename}</a>
                        ) : (
                          <label className="text-xs cursor-pointer" style={{ color: "var(--primary)" }}>
                            Upload
                            <input type="file" accept=".pdf,image/*" className="hidden" onChange={async (e) => {
                              const f = e.target.files?.[0]; if (!f) return;
                              const fd = new FormData(); fd.append("file", f);
                              await api.post(`/pay-records/${r.id}/invoice`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                              toast.success("Invoice uploaded"); fetchData(); e.target.value = "";
                            }} />
                          </label>
                        )}
                      </td>
                      <td className="text-center" onClick={(ev) => ev.stopPropagation()}><QbSyncChip record={r} kind="pay" onSynced={() => fetchData()} /></td>
                      <td><button onClick={async () => { await api.delete(`/pay-records/${r.id}`); fetchData(); }} className="p-1 rounded hover:bg-red-50" style={{ color: "var(--text-muted)" }}><Trash2 className="w-3.5 h-3.5" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "profitability" && (
          <ProfitabilityTab navigate={navigate} />
        )}

        {tab === "quoter_earnings" && (
          <QuoterEarningsTab navigate={navigate} />
        )}
      </div>

      {/* Add Pay Record Dialog */}
      <Dialog open={showAddPay} onOpenChange={setShowAddPay}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Pay Record — {memberObj?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs uppercase text-slate-500">Pay Date *</Label><Input type="date" value={newPay.pay_date} onChange={e => setNewPay(p=>({...p, pay_date: e.target.value}))} className="rounded-sm mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase text-slate-500">Days Worked</Label><Input type="number" step="0.5" value={newPay.days_worked} onChange={e => { const d = e.target.value; setNewPay(p=>({...p, days_worked: d, gross_pay: String(((parseFloat(d)||0) * (memberRates.daily_rate||0)).toFixed(2))})); }} className="rounded-sm mt-1 font-mono" /></div>
              <div><Label className="text-xs uppercase text-slate-500">Hours Worked</Label><Input type="number" step="0.5" value={newPay.hours_worked} onChange={e => { const h = e.target.value; setNewPay(p=>({...p, hours_worked: h, gross_pay: String(((parseFloat(h)||0) * (memberRates.hourly_rate||0)).toFixed(2))})); }} className="rounded-sm mt-1 font-mono" /></div>
            </div>
            <div><Label className="text-xs uppercase text-slate-500">Gross Pay *</Label><Input type="number" step="0.01" value={newPay.gross_pay} onChange={e => setNewPay(p=>({...p, gross_pay: e.target.value}))} className="rounded-sm mt-1 font-mono" /></div>
            {newPay.gross_pay && (
              <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">
                <div className="flex justify-between"><span>Gross</span><span className="font-mono">{formatCurrency(parseFloat(newPay.gross_pay)||0)}</span></div>
                <div className="flex justify-between" style={{ color: "var(--danger)" }}><span>CIS ({memberRates.cis_rate||20}%)</span><span className="font-mono">-{formatCurrency((parseFloat(newPay.gross_pay)||0)*(memberRates.cis_rate||20)/100)}</span></div>
                <div className="flex justify-between font-bold border-t pt-1" style={{ borderColor: "var(--border)" }}><span>Net Pay</span><span className="font-mono" style={{ color: "var(--success)" }}>{formatCurrency((parseFloat(newPay.gross_pay)||0)*(1-(memberRates.cis_rate||20)/100))}</span></div>
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowAddPay(false)}>Cancel</Button><Button onClick={handleAddPayRecord} className="btn-primary">Add Record</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rates & CIS Dialog */}
      <Dialog open={showRates} onOpenChange={setShowRates}>
        <DialogContent>
          <DialogHeader><DialogTitle>Contractor Rates &amp; CIS</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2" style={{ maxHeight: "60vh", overflowY: "auto" }}>
            {teamMembers.map((m, idx) => {
              const r = contractorRates[m.id] || {};
              return (
                <div key={m.id} style={{ border: "1px solid #E5E7EB", borderRadius: "6px", padding: "16px" }}>
                  <p style={{ fontSize: "14px", fontWeight: 600, color: "var(--text-primary)", marginBottom: "12px" }}>{m.name}</p>
                  <div className="flex gap-3">
                    <div>
                      <Label className="text-[10px] uppercase mb-1 block" style={{ color: "#6B7280" }}>Daily £</Label>
                      <Input type="number" step="0.01" value={r.daily_rate||""} onChange={e => setContractorRates(p=>({...p,[m.id]:{...p[m.id],daily_rate:parseFloat(e.target.value)||0}}))} className="h-9 rounded-md text-sm font-mono" style={{ width: "100px", border: "1px solid var(--border)" }} />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase mb-1 block" style={{ color: "#6B7280" }}>Hourly £</Label>
                      <Input type="number" step="0.01" value={r.hourly_rate||""} onChange={e => setContractorRates(p=>({...p,[m.id]:{...p[m.id],hourly_rate:parseFloat(e.target.value)||0}}))} className="h-9 rounded-md text-sm font-mono" style={{ width: "100px", border: "1px solid var(--border)" }} />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase mb-1 block" style={{ color: "#6B7280" }}>CIS %</Label>
                      <Select value={String(r.cis_rate||20)} onValueChange={v => setContractorRates(p=>({...p,[m.id]:{...p[m.id],cis_rate:parseInt(v)}}))}>
                        <SelectTrigger className="h-9 rounded-md text-sm" style={{ width: "120px", border: "1px solid var(--border)" }}><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="20">20%</SelectItem><SelectItem value="30">30%</SelectItem></SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowRates(false)}>Cancel</Button><Button onClick={handleSaveRates} className="btn-primary">Save Rates</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Days Worked Detail Dialog */}
      <Dialog open={showDaysDetail} onOpenChange={setShowDaysDetail}>
        <DialogContent className="max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Days Worked — {memberObj?.name} — {payMonth}</DialogTitle></DialogHeader>
          <div className="py-2">
            <div className="flex gap-4 mb-4 text-sm">
              <span><strong>{daysData?.days_worked || 0}</strong> days worked</span>
              <span style={{ color: "var(--danger)" }}><strong>{daysData?.absence_days || 0}</strong> absence days</span>
            </div>
            {(daysData?.detail || []).length === 0 ? <p className="text-sm" style={{ color: "var(--text-muted)" }}>No days worked this month</p> :
            <div className="space-y-2">
              {(daysData?.detail || []).map((d, i) => (
                <div key={i} className="border rounded-lg p-2.5" style={{ borderColor: "var(--border)" }}>
                  <p className="text-xs font-bold">{new Date(d.date).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" })}</p>
                  {d.jobs.map((j, ji) => (
                    <p key={ji} className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>{j.address?.split(",")[0]} ({j.start_time}–{j.end_time})</p>
                  ))}
                </div>
              ))}
            </div>}
            {(daysData?.absence_dates || []).length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-bold mb-2" style={{ color: "var(--danger)" }}>Absence Days</p>
                <div className="flex flex-wrap gap-1">
                  {daysData.absence_dates.map((d, i) => (
                    <span key={i} className="badge-pill badge-danger text-[10px]">{new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Expense Dialog */}
      <Dialog open={showAddExpense} onOpenChange={setShowAddExpense}>
        <DialogContent style={{ overflow: "visible", padding: "24px" }}>
          <DialogHeader><DialogTitle>Add Expense</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-xs uppercase text-slate-500">Merchant *</Label><Input value={newExpense.merchant} onChange={e => setNewExpense(p=>({...p, merchant: e.target.value}))} className="rounded-sm mt-1" /></div>
              <div><Label className="text-xs uppercase text-slate-500">Category</Label>
                <Select value={newExpense.category} onValueChange={v => setNewExpense(p=>({...p, category: v}))}>
                  <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="materials">Materials</SelectItem>
                    <SelectItem value="tools">Tools</SelectItem>
                    <SelectItem value="vehicle">Vehicle</SelectItem>
                    <SelectItem value="parking">Parking</SelectItem>
                    <SelectItem value="insurance">Insurance</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs uppercase text-slate-500">Description</Label><Input value={newExpense.description} onChange={e => setNewExpense(p=>({...p, description: e.target.value}))} className="rounded-sm mt-1" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-xs uppercase text-slate-500">Amount (ex VAT) *</Label><Input type="number" step="0.01" value={newExpense.amount} onChange={e => { const v = e.target.value; setNewExpense(p=>({...p, amount: v, vat_amount: (parseFloat(v)||0)*0.2})); }} className="rounded-sm mt-1 font-mono" /></div>
              <div><Label className="text-xs uppercase text-slate-500">VAT</Label><Input type="number" step="0.01" value={newExpense.vat_amount} onChange={e => setNewExpense(p=>({...p, vat_amount: e.target.value}))} className="rounded-sm mt-1 font-mono" /></div>
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">Date</Label>
              <Popover open={expDateOpen} onOpenChange={setExpDateOpen}>
                <PopoverTrigger asChild>
                  <button className="flex items-center gap-2 w-full h-9 mt-1 px-3 border rounded-sm text-sm text-left hover:bg-slate-50" style={{ borderColor: "var(--border)" }}>
                    <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                    <span className={newExpense.date ? "text-slate-700" : "text-slate-400"}>{newExpense.date ? fmtDate(newExpense.date) : "Select date"}</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start" side="bottom" sideOffset={4} style={{ zIndex: 9999 }}>
                  <Calendar mode="single" selected={newExpense.date ? (() => { try { const p = newExpense.date.split("/"); return new Date(2000+parseInt(p[2]||"0"), p[1]-1, p[0]); } catch { return undefined; } })() : undefined} onSelect={(d) => { if (d) setNewExpense(p => ({ ...p, date: `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${String(d.getFullYear()).slice(-2)}` })); setExpDateOpen(false); }} weekStartsOn={1} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddExpense(false)}>Cancel</Button>
            <Button onClick={handleAddExpense} className="btn-primary">Add Expense</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* VAT Quarter Settings Dialog */}
      <Dialog open={showVatSettings} onOpenChange={setShowVatSettings}>
        <DialogContent>
          <DialogHeader><DialogTitle>VAT Quarter Dates</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            {["Q1","Q2","Q3","Q4"].map(qn => (
              <div key={qn} className="grid grid-cols-3 gap-2 items-center">
                <Label className="text-sm font-bold">{qn}</Label>
                <Input value={editQuarters[qn]?.start || ""} onChange={e => setEditQuarters(p=>({...p,[qn]:{...p[qn],start:e.target.value}}))} placeholder="dd/mm" className="rounded-sm text-sm" />
                <Input value={editQuarters[qn]?.end || ""} onChange={e => setEditQuarters(p=>({...p,[qn]:{...p[qn],end:e.target.value}}))} placeholder="dd/mm" className="rounded-sm text-sm" />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVatSettings(false)}>Cancel</Button>
            <Button onClick={handleSaveQuarters} className="btn-primary">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProfitabilityTab({ navigate }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/job-profitability").then(r => {
      if (!cancelled) setRows(r.data || []);
    }).catch(() => toast.error("Failed to load job profitability")).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => rows.filter(r => {
    if (statusFilter !== "all" && r.profit_status !== statusFilter) return false;
    if (fromDate && new Date(r.invoice_date) < new Date(fromDate)) return false;
    if (toDate && new Date(r.invoice_date) > new Date(toDate)) return false;
    return true;
  }), [rows, statusFilter, fromDate, toDate]);

  const totals = useMemo(() => {
    const calc = filtered.filter(r => r.profit_status === "calculated");
    const revenue = calc.reduce((s, r) => s + (r.net_received || r.invoice_total), 0);
    const costs = calc.reduce((s, r) => s + r.labour_cost + r.materials_cost + r.operating_expenses, 0);
    const netProfit = calc.reduce((s, r) => s + r.net_profit, 0);
    const avgMargin = calc.length > 0 ? calc.reduce((s, r) => s + r.net_margin, 0) / calc.length : 0;
    return { revenue, costs, netProfit, avgMargin, calculatedCount: calc.length };
  }, [filtered]);

  const exportCsv = () => {
    const header = ["Quote Number", "Address", "Client", "Invoice Date", "Invoice Total", "Net Received", "Labour Cost", "Materials Cost", "Operating Expenses", "Gross Profit", "Net Profit", "Net Margin %", "Status"];
    const lines = filtered.map(r => [r.quote_number, `"${(r.site_address || "").replace(/"/g, '""')}"`, r.client_type, r.invoice_date?.slice(0,10) || "", r.invoice_total.toFixed(2), r.net_received.toFixed(2), r.labour_cost.toFixed(2), r.materials_cost.toFixed(2), r.operating_expenses.toFixed(2), r.gross_profit.toFixed(2), r.net_profit.toFixed(2), r.net_margin.toFixed(1), r.profit_status]);
    const csv = [header, ...lines].map(row => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `job_profitability_${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const marginBadge = (m, status) => {
    if (status !== "calculated") return <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>—</span>;
    let bg = "#DCFCE7", fg = "#166534";
    if (m < 15) { bg = "#FEE2E2"; fg = "#991B1B"; }
    else if (m < 30) { bg = "#FEF3C7"; fg = "#92400E"; }
    return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: bg, color: fg }}>{m.toFixed(0)}%</span>;
  };

  const statusBadge = (s) => {
    const map = {
      not_started: { label: "Not Started", bg: "#F3F4F6", fg: "#6B7280" },
      in_progress: { label: "In Progress", bg: "#FEF3C7", fg: "#92400E" },
      calculated: { label: "Calculated", bg: "#DCFCE7", fg: "#166534" },
    };
    const x = map[s] || map.not_started;
    return <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: x.bg, color: x.fg }}>{x.label}</span>;
  };

  return (
    <div className="space-y-4" data-testid="profitability-tab">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="ui-surface p-4"><p className="ui-label">Total revenue (calculated)</p><p className="text-xl font-mono font-bold mt-1">{formatCurrency(totals.revenue)}</p></div>
        <div className="ui-surface p-4"><p className="ui-label">Total confirmed costs</p><p className="text-xl font-mono font-bold mt-1">{formatCurrency(totals.costs)}</p></div>
        <div className="ui-surface p-4"><p className="ui-label">Total net profit</p><p className="text-xl font-mono font-bold mt-1" style={{ color: totals.netProfit >= 0 ? "#16a34a" : "#DC2626" }}>{formatCurrency(totals.netProfit)}</p></div>
        <div className="ui-surface p-4"><p className="ui-label">Average net margin</p><p className="text-xl font-mono font-bold mt-1">{totals.avgMargin.toFixed(1)}%</p></div>
      </div>

      {/* Filters */}
      <div className="ui-surface p-3 flex items-center gap-3 flex-wrap">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 rounded-md text-sm px-2" style={{ border: "1px solid var(--border)" }} data-testid="profitability-status-filter">
          <option value="all">All statuses</option>
          <option value="not_started">Not Started</option>
          <option value="in_progress">In Progress</option>
          <option value="calculated">Calculated</option>
        </select>
        <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="h-9 rounded-md text-sm" style={{ width: "150px", border: "1px solid var(--border)" }} />
        <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="h-9 rounded-md text-sm" style={{ width: "150px", border: "1px solid var(--border)" }} />
        <Button onClick={exportCsv} variant="outline" size="sm" className="ml-auto gap-1" data-testid="profitability-export-csv"><Download className="w-3.5 h-3.5" /> Export CSV</Button>
      </div>

      {/* Table */}
      <div className="ui-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr style={{ borderBottom: "1px solid var(--border)" }}>
            <th className="text-left p-3 text-[10px] uppercase font-bold" style={{ color: "var(--text-muted)" }}>Job</th>
            <th className="text-left p-3 text-[10px] uppercase font-bold" style={{ color: "var(--text-muted)" }}>Invoice Date</th>
            <th className="text-right p-3 text-[10px] uppercase font-bold" style={{ color: "var(--text-muted)" }}>Total</th>
            <th className="text-right p-3 text-[10px] uppercase font-bold" style={{ color: "var(--text-muted)" }}>Net Received</th>
            <th className="text-right p-3 text-[10px] uppercase font-bold" style={{ color: "var(--text-muted)" }}>Labour</th>
            <th className="text-right p-3 text-[10px] uppercase font-bold" style={{ color: "var(--text-muted)" }}>Materials</th>
            <th className="text-right p-3 text-[10px] uppercase font-bold" style={{ color: "var(--text-muted)" }}>Ops</th>
            <th className="text-right p-3 text-[10px] uppercase font-bold" style={{ color: "var(--text-muted)" }}>Gross</th>
            <th className="text-right p-3 text-[10px] uppercase font-bold" style={{ color: "var(--text-muted)" }}>Net</th>
            <th className="text-center p-3 text-[10px] uppercase font-bold" style={{ color: "var(--text-muted)" }}>Margin</th>
            <th className="text-center p-3 text-[10px] uppercase font-bold" style={{ color: "var(--text-muted)" }}>Status</th>
            <th className="p-3"></th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={12} className="p-4 text-center text-sm" style={{ color: "var(--text-muted)" }}>Loading…</td></tr> :
             filtered.length === 0 ? <tr><td colSpan={12} className="p-4 text-center text-sm" style={{ color: "var(--text-muted)" }}>No invoiced jobs match the filters.</td></tr> :
             filtered.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50" style={{ borderBottom: "1px solid var(--border)" }} data-testid={`profitability-row-${r.id}`}>
                <td className="p-3"><div className="text-xs font-semibold">{r.quote_number}</div><div className="text-[11px]" style={{ color: "var(--text-muted)" }}>{r.site_address}</div></td>
                <td className="p-3 text-xs" style={{ color: "var(--text-muted)" }}>{r.invoice_date?.slice(0,10) || "—"}</td>
                <td className="p-3 text-right font-mono text-xs">{formatCurrency(r.invoice_total)}</td>
                <td className="p-3 text-right font-mono text-xs">{formatCurrency(r.net_received)}</td>
                <td className="p-3 text-right font-mono text-xs">{formatCurrency(r.labour_cost)}</td>
                <td className="p-3 text-right font-mono text-xs">{formatCurrency(r.materials_cost)}</td>
                <td className="p-3 text-right font-mono text-xs">{formatCurrency(r.operating_expenses)}</td>
                <td className="p-3 text-right font-mono text-xs">{r.profit_status === "calculated" ? formatCurrency(r.gross_profit) : "—"}</td>
                <td className="p-3 text-right font-mono text-xs font-bold" style={{ color: r.profit_status === "calculated" ? (r.net_profit >= 0 ? "#16a34a" : "#DC2626") : "var(--text-muted)" }}>{r.profit_status === "calculated" ? formatCurrency(r.net_profit) : "—"}</td>
                <td className="p-3 text-center">{marginBadge(r.net_margin, r.profit_status)}</td>
                <td className="p-3 text-center">{statusBadge(r.profit_status)}</td>
                <td className="p-3 text-right"><Button size="sm" variant="outline" onClick={() => navigate(`/quotes/${r.id}/profit`)} data-testid={`profitability-action-${r.id}`}>{r.profit_status === "calculated" ? "View" : "Complete"}</Button></td>
              </tr>
             ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}



// ─────────────────────────────────────────── Quote Author Earnings tab
function QuoterEarningsTab({ navigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authorId, setAuthorId] = useState("");
  const [expandedMonth, setExpandedMonth] = useState(null);

  const load = useCallback(async () => {
    if (!authorId) return; // wait until an author is selected
    setLoading(true);
    try {
      const r = await api.get(`/financials/quote-author-earnings?author_id=${authorId}`);
      setData(r.data);
    } catch {
      toast.error("Failed to load Quote Author Earnings");
    } finally {
      setLoading(false);
    }
  }, [authorId]);

  useEffect(() => { load(); }, [load]);

  // First-load: fetch the authors list so the dropdown has options, then auto-select Kayhan.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/financials/quote-author-earnings`);
        if (cancelled) return;
        const authors = r.data?.authors || [];
        // Seed the dropdown options (no earnings rows yet — those come from the filtered load)
        setData(prev => prev ? { ...prev, authors } : { authors, by_month: [], totals: { due_now: 0, pending_future: 0, all_time: 0 }, missing_contractor_rates: [] });
        if (!authorId && authors.length) {
          const kh = authors.find(a => a.name === "Kayhan Hassan") || authors[0];
          setAuthorId(kh.id);
        }
      } catch {
        // toast.error already fired by the filtered loader if/when authorId is set
      }
    })();
    return () => { cancelled = true; };
    // Intentionally empty deps — first-load only
  }, []);

  const currentAuthor = useMemo(
    () => (data?.authors || []).find(a => a.id === authorId),
    [data, authorId]
  );

  if (loading && !data) {
    return <div className="ui-surface p-6 text-sm" style={{ color: "var(--text-muted)" }}>Loading earnings…</div>;
  }

  const totals = data?.totals || { due_now: 0, pending_future: 0, all_time: 0 };
  const months = data?.by_month || [];
  const missingRates = data?.missing_contractor_rates || [];
  const authors = data?.authors || [];

  return (
    <div className="space-y-6" data-testid="quoter-earnings-tab">
      <div className="ui-surface p-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Label className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Quote Author</Label>
          <Select value={authorId} onValueChange={setAuthorId}>
            <SelectTrigger className="w-56 h-9 text-sm" data-testid="quoter-earnings-author-select">
              <SelectValue placeholder="Select an author" />
            </SelectTrigger>
            <SelectContent>
              {authors.map(a => (
                <SelectItem key={a.id} value={a.id}>
                  {a.name} <span className="text-xs opacity-60">({a.quote_count})</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto text-xs" style={{ color: "var(--text-muted)" }}>
          Earnings = 20% of profit on no-commission clients, 10% on commission clients. Payable on the last working day of the month after the invoice month.
        </div>
      </div>

      {missingRates.length > 0 && (
        <div className="ui-surface p-4 flex items-start gap-3" style={{ borderLeft: "3px solid #D97706", background: "#FFFBEB" }} data-testid="quoter-earnings-rates-warning">
          <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: "#D97706" }} />
          <div className="text-sm" style={{ color: "#92400E" }}>
            <p className="font-semibold mb-1">Profit numbers are over-stated</p>
            <p>Labour cost is calculated as £0 for these contractors because they have no Daily/Hourly rate set in Settings → Contractor Pay → Rates: <strong>{missingRates.join(", ")}</strong>. Until rates are set, profit (and the amount owed) will look artificially high.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="ui-surface p-4" data-testid="quoter-earnings-due-now">
          <p className="text-[11px] uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Due This Month</p>
          <p className="text-3xl font-bold mt-1 font-mono" style={{ color: totals.due_now > 0 ? "#DC2626" : "var(--text-secondary)" }}>{formatCurrency(totals.due_now)}</p>
          <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>Pay {currentAuthor?.name || "author"} by end of month</p>
        </div>
        <div className="ui-surface p-4" data-testid="quoter-earnings-pending">
          <p className="text-[11px] uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Pending (Future Months)</p>
          <p className="text-3xl font-bold mt-1 font-mono" style={{ color: "#0891B2" }}>{formatCurrency(totals.pending_future)}</p>
          <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>Accruing — not yet payable</p>
        </div>
        <div className="ui-surface p-4" data-testid="quoter-earnings-all-time">
          <p className="text-[11px] uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>All-Time Earned</p>
          <p className="text-3xl font-bold mt-1 font-mono" style={{ color: "#15803D" }}>{formatCurrency(totals.all_time)}</p>
          <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>Across {months.reduce((s, m) => s + m.jobs.length, 0)} invoiced jobs</p>
        </div>
      </div>

      {months.length === 0 ? (
        <div className="ui-surface p-8 text-center text-sm" style={{ color: "var(--text-muted)" }}>
          No invoiced jobs found for {currentAuthor?.name || "this author"} yet.
        </div>
      ) : (
        <div className="space-y-3">
          {months.map(m => {
            const isExpanded = expandedMonth === m.month_key;
            const bucketColor = m.is_due_now || m.is_overdue ? "#DC2626" : "#0891B2";
            const bucketLabel = m.is_overdue ? "OVERDUE" : m.is_due_now ? "DUE THIS MONTH" : "FUTURE";
            return (
              <div key={m.month_key} className="ui-surface overflow-hidden" data-testid={`quoter-earnings-month-${m.month_key}`}>
                <button
                  onClick={() => setExpandedMonth(isExpanded ? null : m.month_key)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors text-left"
                  data-testid={`quoter-earnings-month-toggle-${m.month_key}`}
                >
                  <div className="flex items-center gap-3">
                    <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                    <div>
                      <p className="text-sm font-semibold">{m.month_label}</p>
                      <p className="text-xs" style={{ color: "var(--text-muted)" }}>
                        {m.jobs.length} job{m.jobs.length !== 1 ? "s" : ""} invoiced · Payable {fmtDate(m.payable_date)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-[10px] font-bold tracking-wide px-2 py-0.5 rounded" style={{ background: bucketColor, color: "#fff" }}>
                      {bucketLabel}
                    </span>
                    <div className="text-right">
                      <p className="text-[10px] uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Owed</p>
                      <p className="text-lg font-bold font-mono" style={{ color: bucketColor }}>{formatCurrency(m.total_owed)}</p>
                    </div>
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t" style={{ borderColor: "var(--border)" }}>
                    <table className="w-full text-xs">
                      <thead style={{ background: "var(--bg)" }}>
                        <tr style={{ borderBottom: "1px solid var(--border)" }}>
                          <th className="text-left p-2 text-[10px] uppercase font-bold" style={{ color: "var(--text-muted)" }}>Job</th>
                          <th className="text-left p-2 text-[10px] uppercase font-bold" style={{ color: "var(--text-muted)" }}>Client</th>
                          <th className="text-right p-2 text-[10px] uppercase font-bold" style={{ color: "var(--text-muted)" }}>Invoice ex VAT</th>
                          <th className="text-right p-2 text-[10px] uppercase font-bold" style={{ color: "var(--text-muted)" }}>Labour</th>
                          <th className="text-right p-2 text-[10px] uppercase font-bold" style={{ color: "var(--text-muted)" }}>Materials</th>
                          <th className="text-right p-2 text-[10px] uppercase font-bold" style={{ color: "var(--text-muted)" }}>Client Comm.</th>
                          <th className="text-right p-2 text-[10px] uppercase font-bold" style={{ color: "var(--text-muted)" }}>Profit</th>
                          <th className="text-center p-2 text-[10px] uppercase font-bold" style={{ color: "var(--text-muted)" }}>Rate</th>
                          <th className="text-right p-2 text-[10px] uppercase font-bold" style={{ color: "var(--text-muted)" }}>Owed</th>
                          <th className="p-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {m.jobs.map(j => (
                          <tr key={j.quote_id} className="hover:bg-slate-50" style={{ borderBottom: "1px solid var(--border)" }} data-testid={`quoter-earnings-job-${j.quote_id}`}>
                            <td className="p-2">
                              <div className="font-semibold">{j.quote_number}</div>
                              <div className="text-[10px]" style={{ color: "var(--text-muted)" }}>{j.site_address}</div>
                            </td>
                            <td className="p-2">
                              <div>{j.client_type}</div>
                              <div className="text-[10px]" style={{ color: j.client_charges_commission ? "#D97706" : "#15803D" }}>
                                {j.client_charges_commission ? `${j.commission_type}` : "no commission"}
                              </div>
                            </td>
                            <td className="p-2 text-right font-mono">{formatCurrency(j.invoice_ex_vat)}</td>
                            <td className="p-2 text-right font-mono" style={{ color: j.labour_cost_warning ? "#D97706" : "inherit" }}>
                              {j.labour_cost_warning ? <span title="No contractor rate set">{formatCurrency(j.labour_cost)} ⚠</span> : `-${formatCurrency(j.labour_cost)}`}
                            </td>
                            <td className="p-2 text-right font-mono">-{formatCurrency(j.materials_cost)}</td>
                            <td className="p-2 text-right font-mono">{j.client_commission > 0 ? `-${formatCurrency(j.client_commission)}` : "—"}</td>
                            <td className="p-2 text-right font-mono font-bold" style={{ color: j.profit >= 0 ? "#15803D" : "#DC2626" }}>{formatCurrency(j.profit)}</td>
                            <td className="p-2 text-center">
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded" style={{ background: j.author_rate_pct === 20 ? "#DCFCE7" : "#FEF3C7", color: j.author_rate_pct === 20 ? "#15803D" : "#92400E" }}>
                                {j.author_rate_pct}%
                              </span>
                            </td>
                            <td className="p-2 text-right font-mono font-bold">{formatCurrency(j.amount_owed)}</td>
                            <td className="p-2 text-right">
                              <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => navigate(`/quotes/${j.quote_id}`)} data-testid={`quoter-earnings-view-${j.quote_id}`}>
                                View
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: "var(--bg)", borderTop: "2px solid var(--border)" }}>
                          <td colSpan={6} className="p-2 text-right text-xs font-semibold" style={{ color: "var(--text-muted)" }}>Month total:</td>
                          <td className="p-2 text-right font-mono font-bold text-sm" style={{ color: m.total_profit >= 0 ? "#15803D" : "#DC2626" }}>{formatCurrency(m.total_profit)}</td>
                          <td></td>
                          <td className="p-2 text-right font-mono font-bold text-sm" style={{ color: bucketColor }}>{formatCurrency(m.total_owed)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
